import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Card, 
  CardContent, 
  Avatar,
  Rating,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  LocalHospital,
  WhatsApp
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import ImageCarousel from '../components/ImageCarousel';

interface AnimatedNumberProps {
  value: string;
  duration?: number;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({ value, duration = 6000 }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLSpanElement>(null);

  // Extraer el prefijo, número y sufijo (ej: "+15 años" -> prefix: "+", number: 15, suffix: " años")
  // También maneja casos como "500+" o "100%"
  const match = value.match(/^([^\d]*)(\d+)(.*)$/);
  const prefix = match ? match[1] : '';
  const targetNumber = match ? parseInt(match[2], 10) : 0;
  const suffix = match ? match[3] : '';

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    const currentElement = elementRef.current;
    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const startValue = 0;
    const endValue = targetNumber;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Función de easing para una animación suave y uniforme
      // Usando easeOutCubic para una animación más uniforme sin detenerse al final
      const easeOutCubic = 1 - Math.pow(1 - progress, 2);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOutCubic);

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, targetNumber, duration]);

  return (
    <span ref={elementRef}>
      {prefix}{count}{suffix}
    </span>
  );
};

const Home: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const carouselImagesDesktop = [
    {
      url: '/banner1.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    },
    {
      url: '/banner2.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    },
    {
      url: '/banner3.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    },
    {
      url: '/banner4.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    }
  ];

  const carouselImagesMobile = [
    {
      url: '/banner1mobile.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    },
    {
      url: '/banner2mobile.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    },
    {
      url: '/banner4mobile.jpg',
      backgroundSize: 'cover' as const,
      backgroundPosition: 'center'
    }
  ];

  const testimonials = [
    {
      name: 'María González',
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face',
      title: 'Excelente atención y resultados',
      text: 'Estoy muy contenta con el tratamiento que recibí en KMO Smile. El personal es muy amable y los resultados superaron mis expectativas.',
      rating: 5
    },
    {
      name: 'Carlos Rodríguez',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
      title: 'Profesionales muy capacitados',
      text: 'El equipo de KMO Smile es muy profesional y me brindó un servicio de alta calidad. Estoy muy satisfecho con los resultados.',
      rating: 5
    },
    {
      name: 'Ana Martínez',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face',
      title: 'Recomiendo ampliamente',
      text: 'Recomiendo ampliamente KMO Smile. El ambiente es muy agradable y los profesionales son muy atentos y competentes.',
      rating: 5
    }
  ];

  const screenSize = window.innerHeight;

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          height: { height: screenSize+'px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          width: { xs: '100%', md: '100vw' },
          marginLeft: { xs: 0, md: 'calc(50% - 50vw)' },
          marginRight: { xs: 0, md: 'calc(50% - 50vw)' },
          borderRadius: { xs: '10px', md: 0 }
        }}
      >
        {/* Background Image Carousel */}
        <ImageCarousel
          images={isMobile ? carouselImagesMobile : carouselImagesDesktop}
          interval={6000}
        />

        {/* Hero Content */}
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, textAlign: 'center', py: 8 }}>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={RouterLink}
              to="/servicios"
              variant="contained"
              size="large"
              sx={{
                backgroundColor: 'rgba(255,255,255,0.1)',
                color: 'white',
                borderRadius: '25px',
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#CD853F' }
              }}
            >
              Servicios
            </Button>
            <Button
              component="a"
              href="https://api.whatsapp.com/send?phone=573146628875&text=Hola%20vi%20tu%20sitio%20web%20quisiera%20mas%20informaci%c3%b3n%20"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
              size="large"
              startIcon={<WhatsApp />}
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                borderRadius: '25px',
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderWidth: '2px',
                '&:hover': { 
                  borderColor: 'white', 
                  backgroundColor: 'rgba(255,255,255,0.1)' 
                }
              }}
            >
              Cita WhatsApp
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography 
          variant="h3" 
          component="h2" 
          sx={{ 
            fontWeight: 'bold',
            color: '#2C3E50',
            mb: 5,
            textAlign: 'left'
          }}
        >
          Testimonios
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
          {testimonials.map((testimonial, index) => (
            <Card 
              key={index} 
              sx={{ 
                maxWidth: 350,
                flex: '1 1 300px',
                backgroundColor: '#FFE4B5',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.15)'
                }
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Avatar
                  src={testimonial.image}
                  alt={testimonial.name}
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    mx: 'auto', 
                    mb: 3,
                    border: '3px solid white',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                  }}
                />
                
                <Typography 
                  variant="h6" 
                  component="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#2C3E50',
                    mb: 2
                  }}
                >
                  {testimonial.title}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  color="text.secondary" 
                  sx={{ 
                    lineHeight: 1.6,
                    mb: 2
                  }}
                >
                  {testimonial.text}
                </Typography>
                
                <Rating 
                  value={testimonial.rating} 
                  readOnly 
                  sx={{ color: '#D2691E' }}
                />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      {/* Quick Info Section */}
      <Box sx={{ backgroundColor: '#F8F9FA', py: 6 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ textAlign: 'center', flex: '1 1 200px' }}>
              <LocalHospital sx={{ fontSize: 60, color: '#D2691E', mb: 2 }} />
              <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                <AnimatedNumber value="+15 años de experiencia" duration={6000} />
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Cuidando sonrisas con profesionalismo
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', flex: '1 1 200px' }}>
              <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', color: '#D2691E', mb: 1 }}>
                <AnimatedNumber value="500+" duration={6000} />
              </Typography>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                Pacientes satisfechos
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Confían en nuestro equipo
              </Typography>
            </Box>
            
            <Box sx={{ textAlign: 'center', flex: '1 1 200px' }}>
              <Typography variant="h2" component="div" sx={{ fontWeight: 'bold', color: '#D2691E', mb: 1 }}>
                <AnimatedNumber value="100%" duration={6000} />
              </Typography>
              <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: '#2C3E50' }}>
                Garantía de calidad
              </Typography>
              <Typography variant="body1" color="text.secondary">
                En todos nuestros tratamientos
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 